import { useEffect, useState } from "react";
import {
  lifeNotionEmbedUrl,
  type LifePlace,
} from "../../models/life";

type ShopState =
  | { status: "loading" }
  | { status: "ready"; place: LifePlace }
  | { status: "not-found" }
  | { status: "error" };

function isLifePlace(value: unknown): value is LifePlace {
  if (!value || typeof value !== "object") return false;
  const place = value as Partial<LifePlace>;
  return typeof place.id === "string"
    && typeof place.name === "string"
    && typeof place.href === "string"
    && Array.isArray(place.categories)
    && place.categories.every((category) => typeof category === "string")
    && Array.isArray(place.categoryIds)
    && place.categoryIds.every((categoryId) => typeof categoryId === "string");
}

export function LifeShopPage({ notionId }: { notionId: string }) {
  const [state, setState] = useState<ShopState>({ status: "loading" });
  const [frameLoaded, setFrameLoaded] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    void fetch(`/api/life-in-dulan/shop/${notionId}`, { signal: controller.signal })
      .then(async (response) => {
        if (response.status === 404) {
          setState({ status: "not-found" });
          return;
        }
        if (!response.ok) throw new Error(`Life shop HTTP ${response.status}`);
        const payload = await response.json() as { place?: unknown };
        if (!isLifePlace(payload.place)) throw new Error("Invalid Life shop payload");
        setState({ status: "ready", place: payload.place });
      })
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          setState({ status: "error" });
        }
      });

    return () => controller.abort();
  }, [notionId]);

  const place = state.status === "ready" ? state.place : undefined;
  const embedUrl = place ? lifeNotionEmbedUrl(place.id) : null;

  return (
    <div className="life-shop-embed-page">
      <h1 className="sr-only">{place?.name ?? "都蘭店家介紹"}</h1>

      {state.status === "loading" && (
        <div className="life-shop-message" role="status">
          <span className="life-shop-loader" aria-hidden="true" />
          正在載入店家內容…
        </div>
      )}

      {state.status === "not-found" && (
        <div className="life-shop-message">
          <h2>找不到這間店家</h2>
          <p>這筆內容可能已下架或連結有誤，請回到都蘭生活重新選擇。</p>
          <a className="outline-dark" href="/life-in-dulan">查看都蘭生活</a>
        </div>
      )}

      {state.status === "error" && (
        <div className="life-shop-message">
          <h2>店家內容暫時無法載入</h2>
          <p>你仍可回到都蘭生活查看其他推薦，稍後再回來看看。</p>
          <a className="outline-dark" href="/life-in-dulan">查看都蘭生活</a>
        </div>
      )}

      {state.status === "ready" && place && embedUrl && (
        <div className={`life-notion-frame${frameLoaded ? " is-loaded" : ""}`}>
          {!frameLoaded && (
            <div className="life-notion-frame-loading" role="status">
              <span className="life-shop-loader" aria-hidden="true" />
              正在開啟小星筆記…
            </div>
          )}
          <iframe
            src={embedUrl}
            title={`${place.name}的小星在地筆記`}
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            onLoad={() => setFrameLoaded(true)}
          />
        </div>
      )}
    </div>
  );
}
